import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronRight,
  ClipboardPen,
  LoaderCircle,
  Save,
  ShieldAlert,
  X,
} from "lucide-react";
import { api } from "../api";
import type { ReviewRow } from "../types";
import { StatusBadge } from "../components/StatusBadge";

interface ReviewsPageProps {
  searchValue: string;
}

export function ReviewsPage({ searchValue }: ReviewsPageProps) {
  const queryClient = useQueryClient();
  const reviewQuery = useQuery({
    queryKey: ["reviews"],
    queryFn: api.getReviews,
  });
  const [statusFilter, setStatusFilter] = useState("全部");
  const [selected, setSelected] = useState<ReviewRow | null>(null);

  const rows = useMemo(
    () => reviewQuery.data?.reviews ?? [],
    [reviewQuery.data?.reviews],
  );
  const filteredRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesSearch =
        !query ||
        `${row["商品 ID"]} ${row["脚本版本"]} ${row["脚本修订"]} ${row["修改意见"]}`
          .toLowerCase()
          .includes(query);
      const matchesStatus =
        statusFilter === "全部" || row["审核状态"] === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [rows, searchValue, statusFilter]);

  const pendingCount = rows.filter((row) => row["审核状态"] !== "已通过").length;
  const riskCount = rows.filter((row) => row["是否有风险词"] === "是").length;

  return (
    <div className="review-layout">
      <section className="review-stage">
        <div className="review-summary-strip">
          <div>
            <span>REVIEW QUEUE</span>
            <strong>{rows.length}</strong>
            <small>全部版本</small>
          </div>
          <div>
            <span>PENDING</span>
            <strong>{pendingCount}</strong>
            <small>等待人工确认</small>
          </div>
          <div>
            <span>RISK FLAG</span>
            <strong>{riskCount}</strong>
            <small>命中风险词</small>
          </div>
          <div className="review-principle">
            <ShieldAlert size={21} aria-hidden="true" />
            <p>AI 初稿不直接发布；审核决定始终由团队成员完成。</p>
          </div>
        </div>

        <div className="review-toolbar">
          <div className="filter-chips" aria-label="审核状态筛选">
            {["全部", "待人工审核", "审核中", "已通过", "需修改", "已驳回"].map(
              (status) => (
                <button
                  type="button"
                  key={status}
                  className={statusFilter === status ? "is-active" : ""}
                  onClick={() => setStatusFilter(status)}
                >
                  {status}
                </button>
              ),
            )}
          </div>
          <span>{filteredRows.length} 条记录</span>
        </div>

        {reviewQuery.isLoading && (
          <div className="state-panel">
            <LoaderCircle className="is-spinning" size={24} aria-hidden="true" />
            <strong>正在载入审核队列</strong>
          </div>
        )}

        {reviewQuery.isError && (
          <div className="state-panel is-error">
            <ShieldAlert size={24} aria-hidden="true" />
            <strong>{reviewQuery.error.message}</strong>
          </div>
        )}

        {!reviewQuery.isLoading && (
          <div className="review-table-wrap">
            <table className="review-table">
              <thead>
                <tr>
                  <th>商品 / 版本</th>
                  <th>风险</th>
                  <th>人工确认项</th>
                  <th>评分</th>
                  <th>状态</th>
                  <th aria-label="编辑" />
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => (
                  <tr
                    key={`${row["商品 ID"]}-${row["脚本版本"]}-${row["脚本修订"]}`}
                    className={
                      selected?.["商品 ID"] === row["商品 ID"] &&
                      selected?.["脚本版本"] === row["脚本版本"] &&
                      selected?.["脚本修订"] === row["脚本修订"]
                        ? "is-selected"
                        : ""
                    }
                  >
                    <td>
                      <button
                        type="button"
                        className="review-product-cell"
                        onClick={() => setSelected(row)}
                      >
                        <span>{row["商品 ID"]}</span>
                        <strong>{row["脚本版本"]}</strong>
                        <small>{row["脚本修订"]}</small>
                      </button>
                    </td>
                    <td>
                      {row["是否有风险词"] === "是" ? (
                        <span className="risk-mark is-risk">
                          <ShieldAlert size={14} aria-hidden="true" />
                          {row["命中风险词"] || "需检查"}
                        </span>
                      ) : (
                        <span className="risk-mark">
                          <CheckCircle2 size={14} aria-hidden="true" />
                          未命中
                        </span>
                      )}
                    </td>
                    <td>
                      <p className="clamped-copy">{row["需人工确认事项"]}</p>
                    </td>
                    <td className="score-cell">
                      <strong>{row["脚本可用性评分（1-5）"]}</strong>
                      <span>/ 5</span>
                    </td>
                    <td>
                      <StatusBadge status={row["审核状态"]} compact />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="row-action"
                        onClick={() => setSelected(row)}
                        aria-label={`编辑 ${row["商品 ID"]} ${row["脚本版本"]}`}
                      >
                        <ChevronRight size={16} aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <aside className={selected ? "review-drawer is-open" : "review-drawer"}>
        {selected ? (
          <ReviewEditor
            key={`${selected["商品 ID"]}-${selected["脚本版本"]}-${selected["脚本修订"]}`}
            row={selected}
            onClose={() => setSelected(null)}
            onSaved={async () => {
              await queryClient.invalidateQueries({ queryKey: ["reviews"] });
              await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
              await queryClient.invalidateQueries({ queryKey: ["product"] });
              setSelected(null);
            }}
          />
        ) : (
          <div className="drawer-empty">
            <ClipboardPen size={24} aria-hidden="true" />
            <strong>选择一条审核记录</strong>
            <p>在右侧更新状态、评分和修改意见。</p>
          </div>
        )}
      </aside>
    </div>
  );
}

function ReviewEditor({
  row,
  onClose,
  onSaved,
}: {
  row: ReviewRow;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [status, setStatus] = useState(row["审核状态"]);
  const [score, setScore] = useState(row["脚本可用性评分（1-5）"]);
  const [notes, setNotes] = useState(row["修改意见"]);
  const [message, setMessage] = useState("");

  const updateMutation = useMutation({
    mutationFn: api.updateReview,
    onSuccess: async () => {
      setMessage("审核结果已保存。");
      await onSaved();
    },
    onError: (error: Error) => setMessage(error.message),
  });

  return (
    <form
      className="review-editor"
      onSubmit={(event) => {
        event.preventDefault();
        updateMutation.mutate({
          product_id: row["商品 ID"],
          script_version: row["脚本版本"],
          script_revision_id: row["脚本修订"],
          review_status: status,
          modification_notes: notes,
          usability_score: Number.parseFloat(score),
        });
      }}
    >
      <div className="drawer-heading">
        <div>
          <span>{row["商品 ID"]}</span>
          <h2>{row["脚本版本"]}</h2>
          <code>{row["脚本修订"]}</code>
        </div>
        <button
          type="button"
          className="icon-button"
          onClick={onClose}
          aria-label="关闭审核编辑器"
        >
          <X size={17} aria-hidden="true" />
        </button>
      </div>

      <div className="review-context">
        <span>需人工确认</span>
        <p>{row["需人工确认事项"]}</p>
      </div>

      <label className="field-stack">
        <span>审核状态</span>
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option>待人工审核</option>
          <option>审核中</option>
          <option>已通过</option>
          <option>需修改</option>
          <option>已驳回</option>
        </select>
      </label>

      <label className="field-stack">
        <span>脚本可用性评分</span>
        <input
          type="number"
          value={score}
          min="1"
          max="5"
          step="0.1"
          onChange={(event) => setScore(event.target.value)}
        />
      </label>

      <label className="field-stack field-grow">
        <span>修改意见</span>
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          rows={8}
          placeholder="记录事实核验、风险修改或品牌调性意见"
        />
      </label>

      {message && <p className="panel-message">{message}</p>}
      <button
        type="submit"
        className="primary-button full-button"
        disabled={updateMutation.isPending}
      >
        {updateMutation.isPending ? (
          <LoaderCircle className="is-spinning" size={17} aria-hidden="true" />
        ) : (
          <Save size={17} aria-hidden="true" />
        )}
        保存审核结果
      </button>
    </form>
  );
}
